import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Clock, ArrowRight } from 'lucide-react'
import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'
import { articles } from '@/data/articles'

function ContentBlock({ block }) {
  switch (block.type) {
    case 'intro':
      return (
        <p className="text-lg md:text-xl font-body text-[#0A0A0A]/70 leading-relaxed border-l-2 border-[#0B1F3A] pl-6 mb-10">
          {block.text}
        </p>
      )
    case 'heading':
      return (
        <h2 className="text-xl md:text-2xl font-heading font-black tracking-tight text-[#0A0A0A] mt-14 mb-5 leading-tight">
          {block.text}
        </h2>
      )
    case 'text':
      return (
        <p className="text-base font-body text-[#0A0A0A]/65 leading-relaxed mb-6">
          {block.text}
        </p>
      )
    case 'list':
      return (
        <ul className="space-y-3 mb-8 ml-1">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 bg-[#0B1F3A] flex-shrink-0 mt-2" />
              <span className="text-sm font-body text-[#0A0A0A]/65 leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      )
    case 'table':
      return (
        <div className="mb-10 border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full">
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F6F7F9]'}>
                  <td className="px-5 py-3 text-sm font-body text-[#0A0A0A]/70 border-r border-[#E5E7EB]">{row[0]}</td>
                  <td className="px-5 py-3 text-sm font-body font-semibold text-[#0B1F3A]">{row[1]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    default:
      return null
  }
}

export default function InsightsArticlePage() {
  const { slug } = useParams()
  const article = articles.find(a => a.slug === slug)
  const related = articles.filter(a => a.slug !== slug).slice(0, 2)

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center px-6 py-40">
          <p className="text-sm font-body text-[#0A0A0A]/40 mb-6">Article not found</p>
          <Link to="/insights" className="text-sm font-body font-semibold text-[#0B1F3A] underline">
            Back to Insights
          </Link>
        </div>
        <Footer />
      </main>
    )
  }

  return (
    <main data-testid="article-page">
      <Navbar />

      {/* Header */}
      <section className="pt-40 pb-16 px-6 md:px-12 lg:px-24 bg-[#0B1F3A]">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-white transition-colors font-body font-semibold mb-10"
            data-testid="article-back-link"
          >
            <ArrowLeft size={12} /> All Insights
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="text-[9px] tracking-[0.3em] uppercase font-body font-semibold text-[#16365F]/80 bg-white/10 px-3 py-1.5">
                {article.category}
              </span>
              <span className="text-[9px] tracking-wide text-white/35 font-body flex items-center gap-1.5">
                <Clock size={11} /> {article.readingTime}
              </span>
            </div>
            <h1 className="text-[clamp(2rem,5vw,4rem)] font-heading font-black tracking-tighter leading-tight text-white mb-6" data-testid="article-title">
              {article.title}
            </h1>
            <p className="text-sm font-body text-white/40">
              {article.author} &middot; {article.date}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-16">
            <motion.article
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {article.content.map((block, i) => (
                <ContentBlock key={i} block={block} />
              ))}
            </motion.article>

            {/* Sidebar */}
            <aside className="space-y-12">
              {/* Contextual CTA */}
              <div className="bg-[#0B1F3A] p-8">
                <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-body font-semibold mb-3">
                  Discuss Your Mandate
                </p>
                <p className="text-sm font-body text-white/70 leading-relaxed mb-6">
                  Looking to build your leadership team? We respond within one business day.
                </p>
                <Link
                  to="/contact"
                  data-testid="article-sidebar-cta"
                  className="inline-flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase bg-white text-[#0A0A0A] px-5 py-3 font-body font-semibold hover:bg-[#F6F7F9] transition-colors"
                >
                  Get in Touch <ArrowRight size={11} />
                </Link>
              </div>

              {/* Related articles */}
              {related.length > 0 && (
                <div>
                  <p className="text-[9px] tracking-[0.3em] uppercase text-[#0A0A0A]/30 font-body font-semibold mb-5">
                    Related
                  </p>
                  <div className="space-y-0 border-t border-[#E5E7EB]">
                    {related.map(rel => (
                      <Link
                        key={rel.slug}
                        to={`/insights/${rel.slug}`}
                        className="group block py-5 border-b border-[#E5E7EB]"
                      >
                        <p className="text-[9px] tracking-wide uppercase text-[#16365F] font-body mb-1.5">{rel.category}</p>
                        <p className="text-sm font-heading font-bold text-[#0A0A0A] group-hover:text-[#0B1F3A] transition-colors leading-tight">
                          {rel.title}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  )
}
